import { InstructionCounts, INSTRUCTIONS_ORDER, InstructionType } from 'app/src/engine/Instruction';
import { MovableItemLocation } from 'app/src/engine/movable-items/MovableItem';
import GameStateStore, { GameFinishedReason } from 'app/src/game/GameStateStore';
import { executeNextStep } from 'app/src/game/useLevelSequencer';
import { LevelDescription } from 'app/src/LevelDescription';

type Trial = {
	instructions: InstructionType[];
	location: MovableItemLocation;
	nextInstruction: InstructionType;
};

export type SolveResult = {
	solutions: InstructionType[][];
	trials: number;
};

export default class Solver {
	public static sharedInstance = new Solver();

	private _getRemainingInstructions(
		instructions: InstructionType[],
		initialInstructions: InstructionCounts,
	): InstructionCounts {
		const newInstructions = new Map(initialInstructions);

		for (const instructionType of instructions) {
			const instructionCount = newInstructions.get(instructionType);

			if (!instructionCount) {
				throw Error('Invalid instruction counts.');
			}

			if (instructionCount === 1) {
				newInstructions.delete(instructionType);
			} else {
				newInstructions.set(instructionType, instructionCount - 1);
			}
		}

		return newInstructions;
	}

	// Based on current trial and initial instruction counts, add more trials to
	// the list.
	private _buildNextTrials(
		instructions: InstructionType[],
		location: MovableItemLocation,
		remainingInstructions: InstructionCounts,
	): Trial[] {
		const newTrials: Trial[] = [];

		// We'll check for each instruction type if there's remaining instructions.
		for (const instructionType of INSTRUCTIONS_ORDER) {
			const instructionCount = remainingInstructions.get(instructionType);

			if (instructionCount) {
				const newRemainingInstructions = new Map(remainingInstructions);

				if (instructionCount === 1) {
					newRemainingInstructions.delete(instructionType);
				} else {
					newRemainingInstructions.set(instructionType, instructionCount - 1);
				}

				newTrials.push({
					instructions,
					location,
					nextInstruction: instructionType,
				});
			}
		}

		return newTrials;
	}

	private _getRobotLocation(gameStateStore: GameStateStore): MovableItemLocation {
		const robots = Array.from(gameStateStore.robots);
		const robot = robots && robots[0];

		if (!robot) {
			throw new Error('No robot found.');
		}

		const location = gameStateStore.movableItemLocations.get(robot);

		if (!location) {
			throw new Error('Robot has no location.');
		}

		return location;
	}

	public *solveLevel(levelDescription: LevelDescription): IterableIterator<SolveResult> {
		const gameStateStore = new GameStateStore(levelDescription);

		let trialsCount = 0;
		let solutions: InstructionType[][] = [];
		let bestSolutionLength = Number.MAX_SAFE_INTEGER;

		const robots = Array.from(gameStateStore.robots);
		const robot = robots && robots[0];

		if (!robot) {
			throw new Error('No robot found.');
		}

		const initialLocation = this._getRobotLocation(gameStateStore);

		let trials: Trial[] = this._buildNextTrials(
			[],
			initialLocation,
			levelDescription.instructionCounts,
		);

		// Yield before first trial.
		yield {
			solutions,
			trials: trialsCount,
		};

		while (trials.length) {
			const trial = trials.shift()!;
			const trialInstructions = [...trial.instructions, trial.nextInstruction];

			// Start with a fresh game and advance past the "GameStart" phase.
			gameStateStore.reset();
			gameStateStore.advanceToNextPhase();

			// Keep executing until we've run out of instructions or until the game
			// ends.
			while (
				gameStateStore.currentTurn < trialInstructions.length &&
				!gameStateStore.gameIsFinished
			) {
				executeNextStep(gameStateStore, trialInstructions);
			}

			if (gameStateStore.gameIsFinished) {
				// The game is over, but we'll only consider this trial a success if all
				// the stars are collected.
				if (gameStateStore.gameFinishedReason === GameFinishedReason.AllStarsCollected) {
					// If this solution is better than the others, clear the others.
					if (trialInstructions.length < bestSolutionLength) {
						solutions = [];
						bestSolutionLength = trialInstructions.length;
					}

					if (trialInstructions.length <= bestSolutionLength) {
						solutions.push(trialInstructions);
					}
				}
			} else {
				// If the current solutions are longer than this trial, we can keep
				// looking. Otherwise, we should stop.
				if (solutions.length === 0 || solutions[0].length > trialInstructions.length) {
					// After this program, which instructions remain?
					const remainingInstructions = this._getRemainingInstructions(
						trialInstructions,
						levelDescription.instructionCounts,
					);

					// Get the robot's new location.
					const newLocation = this._getRobotLocation(gameStateStore);

					// If we haven't changed location from the previous instruction, no
					// need to add more instructions.
					if (
						trial.location.row === newLocation.row &&
						trial.location.col === newLocation.col &&
						trial.location.orientation === newLocation.orientation
					) {
						continue;
					}

					// Queue up the new instructions.
					const newTrials = this._buildNextTrials(
						trialInstructions,
						newLocation,
						remainingInstructions,
					);
					trials = [...newTrials, ...trials];
				}
			}

			trialsCount += 1;

			// Yield after each trial.
			yield {
				solutions,
				trials: trialsCount,
			};
		}

		return {
			solutions,
			trials: trialsCount,
		};
	}
}
